import { Scale } from "lucide-react"
import { useState, type ReactNode } from "react"
import { PNL_SOURCE_BASIS, type MarketplaceId } from "@nova-ai/shared"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { cn } from "../../lib/utils"
import type { ExchangeRates } from "../../lib/money"
import { usePrefersReducedMotion } from "../../hooks/use-prefers-reduced-motion"
import {
  useMarketplacePnl,
  type MarketplacePnlResponse,
  type PnlBreakdown,
  type PnlMonthPoint,
} from "../../services/use-pnl"
import { Money } from "../ui/money"
import { CHART_TOOLTIP_CONTENT_STYLE } from "../ui/chart-style"
import { PnlMonthsPicker, PNL_MONTHS_DEFAULT } from "./pnl-months-picker"

const COST_SOURCE_LABEL = {
  [PNL_SOURCE_BASIS.order]: "Order-linked estimates",
  [PNL_SOURCE_BASIS.product]: "Product observations",
}

type PnlSectionProps = {
  marketplace: MarketplaceId
  /** Native marketplace currency for the money figures. Optional: the statement
   *  read carries its own, so a page with no dashboard payload to draw on can
   *  omit it and let the server's answer decide. Falls back to EUR. */
  currency?: string
  rates: ExchangeRates | undefined
}

// The whole-marketplace profit & loss (#167): every euro in (total order GMV,
// not just ad-attributed revenue) against every euro out — marketplace fees,
// shipping, COGS, ad spend and the fixed monthly fee — as a finance statement
// with one narrow column per calendar month plus a Period total. The window is
// picked on the card (independent of the dashboard period picker) and the
// server trims leading no-data months, so the statement starts where the data
// starts. The organic-vs-PPC split differences the orders book against the
// advertiser-report slice, so the statement AND the charts below it come
// entirely from `marketplace_pnl_daily`. Loads independently of the revenue
// dashboard so it never blocks the page. This card is whole-marketplace only —
// per-product ad economics live on the Product performance table
// (`top-products-card.tsx`), which follows the page's period picker.
export function PnlSection({ marketplace, currency, rates }: PnlSectionProps) {
  const [months, setMonths] = useState<number>(PNL_MONTHS_DEFAULT)
  const pnl = useMarketplacePnl(marketplace, months)
  const data = pnl.data
  // A caller that already holds the dashboard payload passes its currency so the
  // two cards cannot disagree mid-load; one that does not waits for the
  // statement to name its own.
  const statementCurrency = currency ?? data?.currency ?? "EUR"

  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-card">
      <div className="flex items-center gap-2">
        <Scale className="size-4 text-muted-foreground" />

        <span className="text-sm font-medium text-foreground">Marketplace profit &amp; loss</span>

        <div className="ml-auto">
          {data && data.byMonth.length > 0 ? (
            <PnlMonthsPicker value={months} onChange={setMonths} label="Statement window" />
          ) : (
            <span className="text-xs text-muted-foreground">
              {pnl.isLoading ? "Loading statement…" : "Awaiting source data"}
            </span>
          )}
        </div>
      </div>

      <p className="mt-1.5 max-w-3xl text-xs text-muted-foreground">
        Whole-marketplace revenue against fees, shipping, product cost and ad spend. Estimated cost
        coverage also means estimated COGS, profit and margin. Unknown costs make those totals
        unavailable. Legacy values retain their original calculation. Cost lines include source-only
        items; coverage units count marketplace sales only. Source-only units are shown separately.
      </p>

      <PnlCostControl data={data} />

      <PnlBody data={data} isLoading={pnl.isLoading} currency={statementCurrency} rates={rates} />
    </div>
  )
}

function PnlCostControl({ data }: { data: MarketplacePnlResponse | undefined }) {
  return (
    <>
      {data && <CostEvidenceStatus control={data.costControl} />}

      {data?.costControl.state === "paused" && (
        <p role="status" className="mt-3 text-sm text-foreground">
          Historical cost writes are paused. Published statements remain available.
        </p>
      )}

      {data?.costControl.latestWork?.state === "failed" && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          Statement update failed. Previous statements remain available. Ask an administrator to
          review this update before retrying.
          <span className="mt-1 block break-all text-xs">
            Update reference: {data.costControl.latestWork.id}
          </span>
        </p>
      )}

      {data?.costControl.latestWork?.state === "pending" && (
        <p role="status" className="mt-3 text-sm text-foreground">
          Statement update pending. Showing the previous complete publication.
        </p>
      )}
    </>
  )
}

function CostEvidenceStatus({ control }: { control: MarketplacePnlResponse["costControl"] }) {
  const sourceLabel = control.sourceBasis
    ? COST_SOURCE_LABEL[control.sourceBasis]
    : "Awaiting financial activation"
  const explanation =
    control.sourceBasis === PNL_SOURCE_BASIS.order
      ? " Magento may record costs after the marketplace sale. Sale dates remain unchanged."
      : ""
  return (
    <>
      <p className="mt-3 text-sm text-foreground">
        {`Cost evidence: ${sourceLabel}.${explanation}`}
      </p>

      {control.exceptions.length > 0 && (
        <details className="mt-3 text-sm text-foreground">
          <summary className="cursor-pointer">Why some product costs are unavailable</summary>

          <ul className="mt-2 space-y-2">
            {control.exceptions.map((exception) => (
              <li key={exception.reason}>
                {`${exception.reason.replaceAll("_", " ")} · ${exception.lines} selections. ${exception.action}`}
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  )
}

function PnlBody({
  data,
  isLoading,
  currency,
  rates,
}: {
  data: MarketplacePnlResponse | undefined
  isLoading: boolean
  currency: string
  rates: ExchangeRates | undefined
}) {
  if (!data || data.byMonth.length === 0) {
    if (isLoading) {
      return <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
    }
    return <EmptyState />
  }
  return (
    <>
      <PnlStatement report={data} currency={currency} rates={rates} />

      <PnlCharts byMonth={data.byMonth} currency={currency} />
    </>
  )
}

// ── Statement table ──────────────────────────────────────────────────────────

type Column = {
  key: string
  label: string
  data: PnlBreakdown
  /** Previous month's column, for the month-over-month growth row. */
  prev: PnlBreakdown | null
}

function PnlStatement({
  report,
  currency,
  rates,
}: {
  report: MarketplacePnlResponse
  currency: string
  rates: ExchangeRates | undefined
}) {
  const columns = buildColumns(report)
  const money = (amount: number | null, className?: string) =>
    amount === null ? (
      <span>Unavailable</span>
    ) : (
      <Money
        amount={amount}
        currency={currency}
        rates={rates}
        layout="inline"
        {...(className ? { className } : {})}
      />
    )

  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full min-w-max border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-xs text-muted-foreground">
            <th className="py-2 pr-4 text-left font-normal">Metric</th>

            {columns.map((c) => (
              <th
                key={c.key}
                className={cn(
                  "py-2 pl-4 text-right font-normal",
                  c.key === "period" && "font-semibold text-foreground"
                )}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          <Row
            label="Revenue (GMV w/o VAT)"
            bold
            columns={columns}
            cell={(d) => money(d.netRevenue)}
          />

          <Row
            label="MoM growth"
            tint
            columns={columns}
            cell={(_d, col) => <MoMCell column={col} />}
          />

          <Row label="Fee summary" bold columns={columns} cell={(d) => money(d.feesTotal)} />

          <Row label="Commission" indent columns={columns} cell={(d) => money(d.commission)} />

          <Row label="Shipping cost" indent columns={columns} cell={(d) => money(d.shippingCost)} />

          <Row label="Fixed fee" indent columns={columns} cell={(d) => money(d.fixedFees)} />

          <Row
            label="Cost coverage"
            columns={columns}
            cell={(d) => (
              <span>
                {
                  {
                    legacy: "Legacy — cost history unverified",
                    unknown: "Incomplete",
                    estimated: "Estimated",
                    proven: "Proven",
                  }[d.costCoverage.qualification]
                }
              </span>
            )}
          />

          <Row
            label="Proven · lines / units"
            columns={columns}
            cell={(d) => `${d.costCoverage.proven.lines} / ${d.costCoverage.proven.units}`}
          />

          <Row
            label="Estimated · lines / units"
            columns={columns}
            cell={(d) => `${d.costCoverage.estimated.lines} / ${d.costCoverage.estimated.units}`}
          />

          <Row
            label="Unknown · lines / units"
            columns={columns}
            cell={(d) => `${d.costCoverage.unknown.lines} / ${d.costCoverage.unknown.units}`}
          />

          <Row
            label="Source-only · lines / units"
            columns={columns}
            cell={(d) =>
              `${d.costCoverage.sourceOnly?.lines ?? 0} / ${d.costCoverage.sourceOnly?.units ?? 0}`
            }
          />

          <Row
            label="Source-only · unknown lines"
            columns={columns}
            cell={(d) => String(d.costCoverage.sourceOnly?.unknownLines ?? 0)}
          />

          <Row
            label="Legacy · lines / units"
            columns={columns}
            cell={(d) =>
              `${d.costCoverage.legacy.lines ?? "Unrecorded"} / ${d.costCoverage.legacy.units}`
            }
          />

          <Row
            label="Missing statement days"
            columns={columns}
            cell={(d) => String(d.costCoverage.missingDays)}
          />

          <Row
            label="Covered product cost"
            columns={columns}
            cell={(d) => money(d.costCoverage.coveredCogs)}
          />

          <Row label="Product cost (COGS)" columns={columns} cell={(d) => money(d.cogs)} />

          <Row label="PPC spend" columns={columns} cell={(d) => money(d.spend)} />

          <Row label="Total ACoS" muted columns={columns} cell={(d) => pctText(d.acosPct)} />

          <Row
            label="Profit"
            bold
            tint
            columns={columns}
            cell={(d) => money(d.netProfit, profitTone(d.netProfit))}
          />

          <Row label="Profit %" tint columns={columns} cell={(d) => pctText(d.marginPct)} />

          {columns.some((column) => column.data.legacy) && (
            <>
              <Row
                label="Legacy — cost history unverified"
                columns={columns}
                cell={() => "Original values"}
              />

              <Row
                label="Legacy product cost"
                columns={columns}
                cell={(d) => money(d.legacy?.cogs ?? null)}
              />

              <Row
                label="Legacy profit"
                columns={columns}
                cell={(d) => money(d.legacy?.netProfit ?? null)}
              />
            </>
          )}

          <SpacerRow span={columns.length + 1} />

          <Row label="Units sold" columns={columns} cell={(d) => countText(d.unitsSold)} />

          <Row label="Orders" columns={columns} cell={(d) => countText(d.ordersCount)} />

          <Row
            label="AOV"
            muted
            columns={columns}
            cell={(d) => (d.aov === null ? dash() : money(d.aov))}
          />

          <SpacerRow span={columns.length + 1} />

          <Row label="PPC sales" bold columns={columns} cell={(d) => money(d.ppcRevenue)} />

          <Row label="PPC units sold" columns={columns} cell={(d) => countText(d.ppcUnits)} />

          <Row label="PPC ACoS" muted columns={columns} cell={(d) => pctText(d.ppcAcosPct)} />

          <Row label="Organic sales" bold columns={columns} cell={(d) => money(d.organicRevenue)} />

          <Row
            label="Organic units sold"
            columns={columns}
            cell={(d) => countText(d.organicUnits)}
          />

          <Row label="PPC sales %" muted columns={columns} cell={(d) => pctText(d.ppcSharePct)} />

          <Row
            label="Organic sales %"
            muted
            columns={columns}
            cell={(d) => pctText(d.ppcSharePct === null ? null : round1(100 - d.ppcSharePct))}
          />
        </tbody>
      </table>
    </div>
  )
}

function Row({
  label,
  columns,
  cell,
  bold,
  indent,
  muted,
  tint,
}: {
  label: string
  columns: Column[]
  cell: (data: PnlBreakdown, column: Column) => ReactNode
  bold?: boolean
  indent?: boolean
  muted?: boolean
  tint?: boolean
}) {
  return (
    <tr className={cn("border-b border-border/40 last:border-0", tint && "bg-success/5")}>
      <td
        className={cn(
          "py-1.5 pr-4 text-left",
          bold && "font-semibold text-foreground",
          indent && "pl-4 italic text-muted-foreground",
          !bold && !indent && "text-muted-foreground"
        )}
      >
        {label}
      </td>

      {columns.map((c) => (
        <td
          key={c.key}
          className={cn(
            "py-1.5 pl-4 text-right tabular-nums",
            bold && "font-semibold",
            muted && "text-muted-foreground"
          )}
        >
          {cell(c.data, c)}
        </td>
      ))}
    </tr>
  )
}

/** A quiet visual break between statement blocks (the P&L waterfall, the volume
 *  block, the sales split) — the spreadsheet's blank row. */
function SpacerRow({ span }: { span: number }) {
  return (
    <tr aria-hidden>
      <td colSpan={span} className="py-1.5" />
    </tr>
  )
}

/** Month-over-month revenue growth for a column, green/red by sign. Blank on
 *  the first month (no prior) and on the Period total column. */
function MoMCell({ column }: { column: Column }) {
  if (!column.prev || column.prev.netRevenue <= 0) {
    return dash()
  }
  const pct = ((column.data.netRevenue - column.prev.netRevenue) / column.prev.netRevenue) * 100
  const rounded = round1(pct)
  return (
    <span className={profitTone(rounded)}>
      {`${rounded > 0 ? "+" : ""}${rounded.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`}
    </span>
  )
}

/** Month columns chronological, plus a bold Period total column on the right
 *  when the window spans more than one month. */
function buildColumns(report: MarketplacePnlResponse): Column[] {
  const monthColumns: Column[] = report.byMonth.map((m, i) => ({
    key: m.month,
    label: monthLabel(m.month, report.from, report.to),
    data: m,
    prev: i > 0 ? (report.byMonth[i - 1] ?? null) : null,
  }))
  if (monthColumns.length <= 1) {
    return monthColumns
  }
  return [...monthColumns, { key: "period", label: "Period", data: report.totals, prev: null }]
}

/** `2026-07` → `Jul '26`, or `Jul '26 · 1–13` when the window covers only part
 *  of the month (derived from the report's from/to). */
function monthLabel(month: string, from: string, to: string): string {
  const year = Number(month.slice(0, 4))
  const monthIndex = Number(month.slice(5, 7)) - 1
  const short = new Date(Date.UTC(year, monthIndex, 1)).toLocaleString("en", { month: "short" })
  const base = `${short} '${month.slice(2, 4)}`
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate()
  const startDay = from.startsWith(month) ? Number(from.slice(8, 10)) : 1
  const endDay = to.startsWith(month) ? Number(to.slice(8, 10)) : lastDay
  if (startDay > 1 || endDay < lastDay) {
    return `${base} · ${startDay}–${endDay}`
  }
  return base
}

function dash(): ReactNode {
  return <span className="text-muted-foreground/40">—</span>
}

function pctText(value: number | null): ReactNode {
  if (value === null) {
    return dash()
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`
}

function countText(value: number): ReactNode {
  if (value === 0) {
    return dash()
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

// ── Monthly charts ────────────────────────────────────────────────────────────

/** The charts the statement's month series feeds directly: the organic-vs-PPC
 *  sales split per month (stack height = GMV) and the margin/ACoS ratio trend. */
function PnlCharts({ byMonth, currency }: { byMonth: PnlMonthPoint[]; currency: string }) {
  const prefersReducedMotion = usePrefersReducedMotion()
  const data = byMonth.map((m) => ({
    label: shortMonth(m.month),
    organic: m.organicRevenue,
    ppc: m.ppcRevenue,
    provenMargin: m.costCoverage.qualification === "proven" ? m.marginPct : null,
    estimatedMargin: m.costCoverage.qualification === "estimated" ? m.marginPct : null,
    qualification: m.costCoverage.qualification,
    acosPct: m.acosPct,
  }))
  return (
    <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div>
        <div className="mb-1 text-xs text-muted-foreground">
          {/* Charts plot native-currency values (Money-style conversion is a
              per-cell device); the label says which currency that is. */}
          Sales by month — organic vs PPC ({currency})
        </div>

        <div className="h-[200px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />

              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />

              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={56} />

              <Tooltip
                formatter={(value, name) => [
                  Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 }),
                  name === "organic" ? "Organic" : "PPC",
                ]}
                contentStyle={CHART_TOOLTIP_CONTENT_STYLE}
                cursor={{ className: "fill-muted" }}
              />

              <Bar
                dataKey="organic"
                stackId="sales"
                fill="var(--color-muted-foreground)"
                radius={[0, 0, 0, 0]}
                isAnimationActive={!prefersReducedMotion}
                animationDuration={460}
                animationEasing="ease-out"
              />

              <Bar
                dataKey="ppc"
                stackId="sales"
                fill="var(--color-info)"
                radius={[3, 3, 0, 0]}
                isAnimationActive={!prefersReducedMotion}
                animationDuration={460}
                animationEasing="ease-out"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div>
        <div className="mb-1 text-xs text-muted-foreground">
          Profit margin &amp; total ACoS, % — dashed margin is estimated
        </div>

        <div className="h-[200px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />

              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />

              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={40} />

              <Tooltip
                formatter={(value, name) => [
                  `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`,
                  name,
                ]}
                contentStyle={CHART_TOOLTIP_CONTENT_STYLE}
              />

              <Line
                type="monotone"
                dataKey="provenMargin"
                name="Proven profit margin"
                stroke="var(--color-success)"
                strokeWidth={2}
                dot={{ r: 2 }}
                isAnimationActive={!prefersReducedMotion}
                animationDuration={540}
                animationEasing="ease-out"
              />

              <Line
                type="monotone"
                dataKey="estimatedMargin"
                name="Estimated profit margin"
                stroke="var(--color-success)"
                strokeWidth={2}
                strokeDasharray="4 3"
                dot={{ r: 2 }}
                isAnimationActive={!prefersReducedMotion}
                animationDuration={540}
                animationEasing="ease-out"
              />

              <Line
                type="monotone"
                dataKey="acosPct"
                name="Total ACoS"
                stroke="var(--color-warning)"
                strokeWidth={2}
                dot={false}
                isAnimationActive={!prefersReducedMotion}
                animationDuration={540}
                animationEasing="ease-out"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

/** `2026-07` → `Jul` for a compact axis tick. */
function shortMonth(month: string): string {
  const year = Number(month.slice(0, 4))
  const monthIndex = Number(month.slice(5, 7)) - 1
  return new Date(Date.UTC(year, monthIndex, 1)).toLocaleString("en", { month: "short" })
}

function profitTone(amount: number | null): string {
  if (amount === null) {
    return "text-muted-foreground"
  }
  if (amount > 0) {
    return "text-success"
  }
  if (amount < 0) {
    return "text-destructive"
  }
  return "text-foreground"
}

function EmptyState() {
  return (
    <div className="mt-4 grid gap-3 rounded-lg border border-dashed border-border bg-muted/[0.18] p-4 sm:grid-cols-[auto_1fr_1fr] sm:items-center">
      <div>
        <div className="text-sm font-medium text-foreground">Statement is waiting for inputs</div>

        <p className="mt-1 text-xs text-muted-foreground">
          P&amp;L appears as its source books arrive.
        </p>
      </div>

      <div className="rounded-md bg-card px-3 py-2 ring-1 ring-border/70">
        <div className="text-xs font-medium text-foreground">1. Orders book</div>

        <div className="mt-0.5 text-[11px] text-muted-foreground">Filled by the orders sync</div>
      </div>

      <div className="rounded-md bg-card px-3 py-2 ring-1 ring-border/70">
        <div className="text-xs font-medium text-foreground">2. PPC spend</div>

        <div className="mt-0.5 text-[11px] text-muted-foreground">
          Added after an advertiser-report import
        </div>
      </div>
    </div>
  )
}

function round1(value: number): number {
  return Math.round(value * 10) / 10
}
